import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSearchParams } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { CRYPTED_KEY } from 'utils/constants';

import { userPresenter, userStore } from 'stores/root_store';

import styles from './login.module.css';
import imesLogo from 'assets/logo.png';
import isdLogo from 'assets/isdcorp_logo.png';
import itpLogo from 'assets/logo_xl.png';
import citekLogo from 'assets/logo2x.png';

import { FaEnvelope, FaLock } from 'react-icons/fa';
import { AiOutlineEyeInvisible, AiOutlineEye } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { TbLoaderQuarter } from 'react-icons/tb';
import { Backdrop, CircularProgress } from '@mui/material';
interface FormLoginModel {
  userName: string;
  password: string;
}

// validation foem bằng thư viện yup
const schema = yup.object().shape({
  userName: yup.string().required('Tên tài khoản không được để trống!'),
  password: yup.string().required('Mật khẩu không được để trống!'),
});

export function createLogin() {
  return observer(() => {
    // Tên trang
    document.title = 'Đăng nhập hệ thống';
    // ============= Lấy parameter đăng nhập ==================//
    // trang https://itp-mes.isdcorp.vn gửi user , password qua
    const [searchParams] = useSearchParams();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const key = CryptoJS.enc.Utf8.parse(CRYPTED_KEY);
    const navigate = useNavigate();
    // ========== Yup validate ===============
    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm<FormLoginModel>({
      resolver: yupResolver(schema),
    });

    // =================== Show hide password =======================
    // hàm show và hide password. nếu đang show thì hide và ngược lại
    const handleShowPassword = () => {
      setShowPassword((pre) => !pre);
    };

    // ================== Submit login form =================
    const onLogin = async (value: FormLoginModel) => {
      setLoading(true);
      await userPresenter.login(userStore, value);
      if (userStore.userRespone.isSuccess) {
        navigate('/');
      } else {
        toast.error(userStore.userRespone.message);
      }
      setLoading(false);
    };

    useEffect(() => {
      const LoginHasStringParams = async () => {
        if (searchParams.get('username')) {
          await userPresenter.login(userStore, {
            // userName: searchParams.get('username'),
            // password: searchParams.get('password'),
            userName: decrypted(searchParams.get('username')),
            password: decrypted(searchParams.get('password')),
          });
          if (userStore.userRespone.isSuccess) {
            // window.close();
            navigate('/');
          } else {
            toast.error(userStore.userRespone.message);
          }
        }
      };

      LoginHasStringParams();
    }, []);
    const encrypted = (data: string) => {
      const encryptedData = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(data), key, {
        keySize: 256 / 8,
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      });
      return encryptedData.toString();
    };

    const decrypted = (data: any) => {
      const decryptedData = CryptoJS.AES.decrypt(data, key, {
        keySize: 256 / 8,
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      });
      return CryptoJS.enc.Utf8.stringify(decryptedData);
    };

    return (
      <div className={styles.wrapper}>
        {process.env.NODE_ENV === 'production' ? (
          <Backdrop sx={{ color: '#blue', background: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        ) : (
          <div className={`shadow-2xl flex max-w-4xl rounded-2xl bg-white ${styles.containerWidth}`}>
            <div className="rounded-2xl xl:w-1/2 md:w-1/2 w-full p-14 py-10 bg-white">
              <div className="w-full flex justify-between mb-6 items-end">
                <img src={itpLogo} alt="TTF Logo" className="h-16" />
                <img src={citekLogo} alt="TTF Logo" className="h-8 mb-2" />
                <img src={isdLogo} alt="ISDCorp Logo" className="h-8 mb-3" />
              </div>
              <form onSubmit={handleSubmit(onLogin)} className="space-y-4">
                <h3 className="text-center text-3xl font-bold text-primary mb-12">
                  Đăng nhập
                  <span className="w-16 h-1 block bg-primary mx-auto mt-3"></span>
                </h3>
                <div className="relative text-gray-500">
                  <input
                    {...register('userName')}
                    type="text"
                    id="userName"
                    placeholder="Tên tài khoản"
                    className="peer w-full border-2 border-gray-200 py-2 rounded-lg pr-3 pl-14 bg-transparent outline-none placeholder-slate-400 focus:border-blue-300"
                  />
                  <label
                    htmlFor="userName"
                    className="peer-focus:text-blue-400 absolute mx-5 text-gray-400 top-1/2 left-0 translate-y-[-50%]"
                  >
                    <FaEnvelope />
                  </label>
                </div>
                {/* thông báo lỗi khi userName không hợp lệ */}
                {errors.userName && <p className="!mt-1 text-xs text-red-500 pl-5">{`${errors.userName.message}`}</p>}
                <div className="relative text-gray-500">
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="Mật khẩu"
                    className="peer w-full border-2 border-gray-200 py-2 rounded-lg pr-12 pl-14 bg-transparent outline-none placeholder-slate-400 focus:border-blue-300"
                  />
                  <label
                    htmlFor="password"
                    className="peer-focus:text-blue-400 absolute mx-5 text-gray-400 top-1/2 left-0 translate-y-[-50%]"
                  >
                    <FaLock />
                  </label>
                  {/* set icon cho sự kiện show và hide password */}
                  {showPassword ? (
                    <button type="button">
                      <AiOutlineEye
                        onClick={handleShowPassword}
                        className="peer-focus:text-blue-400 absolute right-0 top-1/2 translate-y-[-50%] mx-5 text-xl cursor-pointer text-gray-400"
                      />
                    </button>
                  ) : (
                    <button type="button">
                      <AiOutlineEyeInvisible
                        onClick={handleShowPassword}
                        className="peer-focus:text-blue-400 absolute right-0 top-1/2 translate-y-[-50%] mx-5 text-xl cursor-pointer text-gray-400"
                      />
                    </button>
                  )}
                </div>
                {/* thông báo lỗi khi password không hợp lệ */}
                {errors.password && <p className="!mt-1 text-xs text-red-500 pl-5">{`${errors.password.message}`}</p>}
                <div className="w-full flex items-center">
                  <input type="checkbox" className={styles.checkBox} />
                  <h3 className="text-gray-400 text-sm font-medium ml-3">Nhớ thông tin đăng nhập</h3>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full text-white font-medium rounded-lg text-sm px-5 py-2.5 text-center focus:outline-none ${
                    loading ? 'bg-blue-700/80' : 'bg-blue-700 hover:bg-blue-600'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <TbLoaderQuarter className="animate-spin bg" />
                      Đăng nhập...
                    </span>
                  ) : (
                    <span>Đăng nhập</span>
                  )}
                </button>
              </form>
            </div>
            <div
              className={`relative w-1/2 bg-primary rounded-2xl border-r-2 border-white hidden xl:flex md:flex sm:hiden flex-col justify-center items-center p-5 py-24 ${styles.shadow}`}
            >
              <div className="w-4/5 my-[17px]">
                <img src={imesLogo} className="w-full h-full" />
              </div>
              <span
                className={`absolute w-10 h-10 bg-white rounded-full top-9 right-9 shadow-zinc-800 border-l-2 ${styles.shadowInner} border-white`}
              ></span>
            </div>
          </div>
        )}
      </div>
    );
  });
}
